import type {
  ForgetPasswordData,
  GoogleSignInData,
  LoginData,
  RegisterFcmTokenData,
  ResendEmailVerificationData,
  ResetPasswordData,
  SignUpData,
  VerifyEmailData,
  VerifyResetPasswordData,
} from '../types/auth';
import { api, type ApiResponse } from '../utils';

const signUpUser = async (data: SignUpData): Promise<ApiResponse> => {
  const payload = {
    first_name: data.firstName,
    last_name: data.lastName,
    email: data.email,
    password: data.password,
  };

  const { data: response } = await api.post('/auth/signup', payload);
  return response;
};

const signInUser = async (data: LoginData): Promise<ApiResponse> => {
  const payload = {
    email: data.email,
    password: data.password,
  };

  const { data: response } = await api.post('/auth/signin', payload);
  return response;
};

const googleSignIn = async (data: GoogleSignInData): Promise<ApiResponse> => {
  const payload = {
    token: data.token,
  };

  try {
    const response = await api.post('/auth/google-signin', payload);
    return response.data;
  } catch (error: any) {
    return {
      code: error?.code || 500,
      message: error?.message || 'Failed to sign in with Google',
      result: null,
    };
  }
};

const signOutUser = async (): Promise<ApiResponse> => {
  const { data: response } = await api.post('/auth/logout');
  return response;
};

const verifyEmail = async (data: VerifyEmailData): Promise<ApiResponse> => {
  const { data: response } = await api.post('/auth/verify-email', data);
  return response;
};

const forgetPassword = async (
  data: ForgetPasswordData
): Promise<ApiResponse> => {
  const { data: response } = await api.post('/auth/forgot-password', data);
  return response;
};

const verifyResetPasswordCode = async (
  data: VerifyResetPasswordData
): Promise<ApiResponse> => {
  const { data: response } = await api.post(
    '/auth/verify-reset-password-code',
    data
  );
  return response;
};

const resetPassword = async (data: ResetPasswordData): Promise<ApiResponse> => {
  const { data: response } = await api.post('/auth/reset-password', data);
  return response;
};

const resendEmailVerification = async (
  data: ResendEmailVerificationData
): Promise<ApiResponse> => {
  const { data: response } = await api.post(
    '/auth/resend-email-verification-code',
    data
  );
  return response;
};

const checkStatus = async (): Promise<ApiResponse> => {
  const { data: response } = await api.get('/profile/get-profile');
  return response;
};

const registerFcmToken = async (
  data: RegisterFcmTokenData
): Promise<ApiResponse> => {
  const { data: response } = await api.post(
    '/notification/save-fcm-token',
    data
  );
  return response;
};

/**
 * Initiate account deletion for the authenticated user (Apr-20).
 *
 * Apple Guideline 5.1.1(v) + Google Play account-deletion policy. Calls
 * DELETE /users/me which soft-deletes the user immediately (sets deleted_at);
 * JWT strategy blocks soft-deleted users on next request → existing tokens
 * invalidate naturally. Full hard-delete of related data happens after a
 * 30-day grace window via backend SchedulerService sweeper. User can
 * reactivate within that window by replying to the confirmation email.
 *
 * Caller should: logout flow + hard redirect to /login after success.
 *
 * Backend returns:
 *   { success, message, scheduled_hard_delete (ISO), already_initiated }
 */
const deleteAccount = async (): Promise<ApiResponse> => {
  const { data: response } = await api.delete('/users/me');
  return response;
};

export {
  signUpUser,
  signInUser,
  signOutUser,
  verifyEmail,
  forgetPassword,
  verifyResetPasswordCode,
  resetPassword,
  googleSignIn,
  checkStatus,
  resendEmailVerification,
  registerFcmToken,
  deleteAccount,
};
